import { useRef, useState } from 'react';
import { useMe } from '../lib/me.tsx';
import { useAvatars, normalizeName } from '../lib/avatars.tsx';
import { uploadAvatar } from '../lib/api.ts';
import { Avatar } from '../components/Avatar.tsx';
import { AvatarCropper } from '../components/AvatarCropper.tsx';
import { InstallCoach } from '../components/InstallCoach.tsx';
import { NotificationSettings } from '../components/NotificationSettings.tsx';

/** Where a member sets their own profile picture. */
export function Profile() {
  const me = useMe();
  const { map, refresh } = useAvatars();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  if (!me) {
    return <p className="text-sm text-chalk-dim">Loading…</p>;
  }

  const current = preview ?? map[normalizeName(me.name)] ?? null;

  // A file was picked: hand it to the cropper before anything is uploaded.
  function onPick(file: File) {
    setError(null);
    setPending(file);
  }

  // The cropper handed back a framed 160px square: upload just that.
  async function onCropped(dataUrl: string) {
    setStatus('saving');
    setError(null); // clear any prior failure so a retry starts clean
    try {
      await uploadAvatar(dataUrl);
      setPreview(dataUrl);
      setPending(null);
      refresh();
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : 'Upload failed');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="eyebrow">Your profile</p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">
          {me.name || 'You'}
        </h1>
        <p className="mt-1 font-mono text-xs text-chalk-dim">{me.email}</p>
      </div>

      {/* Hidden picker is always mounted so the button can trigger it. Resetting
          value on click lets you re-pick the same file after a cancel. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onClick={(e) => {
          (e.target as HTMLInputElement).value = '';
        }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
        }}
      />

      {pending ? (
        <AvatarCropper
          file={pending}
          saving={status === 'saving'}
          uploadError={status === 'error' ? error : null}
          onCancel={() => {
            setPending(null);
            setError(null);
            setStatus('idle');
          }}
          onConfirm={onCropped}
        />
      ) : (
        <div className="card flex flex-col items-center gap-4 p-6 sm:flex-row sm:items-center">
          {current ? (
            <img
              src={current}
              alt={me.name}
              className="h-24 w-24 shrink-0 rounded-full object-cover"
            />
          ) : (
            <Avatar name={me.name || me.email} size={96} />
          )}
          <div className="space-y-2 text-center sm:text-left">
            <p className="text-sm text-chalk-dim">
              Upload a picture and it shows up next to your name across the book.
              You frame it yourself, and it's shrunk on your device before it
              uploads.
            </p>
            <button
              onClick={() => fileRef.current?.click()}
              className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk hover:opacity-90"
            >
              {current ? 'Change picture' : 'Upload picture'}
            </button>
            {status === 'saved' && (
              <span className="ml-3 font-mono text-xs text-cash">saved</span>
            )}
            {error && <p className="font-mono text-xs text-blood">{error}</p>}
          </div>
        </div>
      )}

      <InstallCoach />
      <NotificationSettings />
    </div>
  );
}
